import { useMemo } from 'react'
import { useAppSelector } from '@/store/hooks'
import { selectTaskActivity, selectTaskActivityLoading } from '@/store/activity/activitySelectors'
import { groupByDay } from '@/store/activity/activityUtils'
import Modal from '@/components/modals/Modal/Modal'
import IconClose from '@/assets/icons/icon-close.svg?react'
import IconButton from '@/components/ui/IconButton/IconButton'
import { TextH2, TextNorm } from '@/components/ui/Text'
import TaskModalActivityEvent from '@/components/modals/TaskModal/components/TaskModalComments/components/TaskModalActivityEvent/TaskModalActivityEvent'
import css from './TaskActivityModal.module.css'

interface Props {
    taskId: string
    onClose: () => void
}

// The task's activity is already subscribed by the open TaskModal (this modal opens on top
// of it from the task menu), so we just read it from the store — no separate subscription.
const TaskActivityModal = ({ taskId, onClose }: Props) => {
    const events = useAppSelector(state => selectTaskActivity(state, taskId))
    const loading = useAppSelector(state => selectTaskActivityLoading(state, taskId))

    const groups = useMemo(() => groupByDay(events), [events])

    return (
        <Modal onClose={onClose} maxWidth={560}>
            <div className={css.close}>
                <IconButton icon={<IconClose />} onClick={onClose} />
            </div>
            <TextH2 className={css.title}>Activity</TextH2>
            {loading && events.length === 0 ? (
                <div className={css.empty}><TextNorm className={css.empty_text}>Loading…</TextNorm></div>
            ) : events.length === 0 ? (
                <div className={css.empty}><TextNorm className={css.empty_text}>No activity yet.</TextNorm></div>
            ) : (
                <div className={css.groups}>
                    {groups.map(group => (
                        <div key={group.label}>
                            <TextH2 className={css.group_label}>{group.label}</TextH2>
                            <div className={css.group_items}>
                                {group.events.map(event => (
                                    <TaskModalActivityEvent key={event.id} event={event} />
                                ))}
                            </div>
                        </div>
                    ))}
                </div>
            )}
        </Modal>
    )
}

export default TaskActivityModal
